<header
    class="h-16 bg-surface-0 dark:bg-surface-900 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between px-3 sm:px-6 transition-colors duration-300">

    <div class="flex items-center gap-2 sm:gap-5">

        <div class="flex md:hidden items-center gap-2 pr-2">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 bg-primary p-1">
                <img src="assets/logo.png" alt="Arrow HR" class="h-full w-full object-contain" />
            </div>
            <span class="text-base font-bold text-text-main tracking-tight">Arrow HR</span>
        </div>

        <nav class="hidden lg:flex items-center gap-2 text-sm">
            <a routerLink="/"
                class="text-text-main font-medium no-underline hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer">
                Dashboard
            </a>

            @for (crumb of breadcrumbs(); track crumb.label) {
                <span class="text-surface-400 dark:text-surface-600">/</span>

                @if (!$last && crumb.url) {
                    <a [routerLink]="crumb.url"
                        class="text-text-main font-medium no-underline hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer">
                        {{ crumb.label }}
                    </a>
                }

                @if ($last || !crumb.url) {
                    <span class="text-text-muted">
                        {{ crumb.label }}
                    </span>
                }
            }
        </nav>
    </div>

    <div class="flex items-center gap-1 sm:gap-2">

        <button
            class="hidden sm:flex w-9 h-9 sm:w-10 sm:h-10 items-center justify-center rounded-lg text-text-muted hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors border-none cursor-pointer"
            pRipple>
            <i class="pi pi-sparkles text-base"></i>
        </button>

        <button (click)="toggleDarkMode()"
            class="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg text-text-muted hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors border-none cursor-pointer"
            pRipple>
            <i [class]="isDarkMode() ? 'pi pi-sun' : 'pi pi-moon'" class="text-base"></i>
        </button>

        <button (click)="themeConfigOp.toggle($event)"
            class="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg text-text-muted hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors border-none cursor-pointer"
            pRipple>
            <i class="pi pi-palette text-base"></i>
        </button>

        <p-popover #themeConfigOp
            styleClass="p-5 border shadow-2xl rounded-2xl dark:bg-surface-900 dark:border-surface-800 w-[340px] max-w-[95vw]">
            <div class="flex flex-col gap-6">
                <div>
                    <span class="text-base font-semibold text-surface-500 dark:text-surface-400 block mb-3">Primary</span>
                    <div class="grid grid-cols-8 gap-3">
                        @for (color of primaryColors; track color.name) {
                            <button (click)="changePrimary(color)"
                                [style.backgroundColor]="color.hex" [class.ring-2]="activePrimary === color.name"
                                class="w-6 h-6 rounded-full cursor-pointer border-none transition-transform hover:scale-110 shadow-sm ring-offset-2 ring-offset-surface-0 dark:ring-offset-surface-900 ring-surface-800 dark:ring-white">
                            </button>
                        }
                    </div>
                </div>

                <div>
                    <span class="text-base font-semibold text-surface-500 dark:text-surface-400 block mb-3">Presets</span>
                    <div class="flex bg-surface-100 dark:bg-surface-800 rounded-xl p-1 gap-1">
                        @for (preset of presets; track preset) {
                            <button (click)="changePreset(preset)"
                                [class.bg-surface-0]="activePreset === preset"
                                [class.dark:bg-surface-700]="activePreset === preset"
                                [class.shadow-sm]="activePreset === preset"
                                [class.text-surface-900]="activePreset === preset"
                                [class.dark:text-surface-0]="activePreset === preset"
                                class="flex-1 text-sm py-2 rounded-lg font-semibold text-surface-500 dark:text-surface-400 transition-all border-none cursor-pointer bg-transparent hover:text-surface-800 dark:hover:text-surface-200">
                                {{preset}}
                            </button>
                        }
                    </div>
                </div>
            </div>
        </p-popover>

        <div class="relative ml-0 sm:ml-1">
            <button (click)="notifOp.toggle($event)"
                class="relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg text-text-muted hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors border-none cursor-pointer"
                pRipple
                [attr.aria-label]="'Notifications' + (notifService.hasUnread() ? ', ' + notifService.unreadCount() + ' unread' : '')"
                aria-haspopup="true">
                <i class="pi pi-bell text-base"></i>
                @if (notifService.hasUnread()) {
                    <span class="absolute top-2 right-2 sm:right-2.5 min-w-[16px] h-4 px-1 bg-red-500 border border-surface-0 dark:border-surface-900 rounded-full flex items-center justify-center text-white text-xs font-bold leading-none" aria-hidden="true">
                        {{ notifService.unreadCount() > 9 ? '9+' : notifService.unreadCount() }}
                    </span>
                }
            </button>

            <p-popover #notifOp [style]="{ width: '384px', maxWidth: '95vw' }"
                styleClass="p-0 border shadow-xl overflow-hidden rounded-xl dark:bg-surface-900 dark:border-surface-800"
                role="dialog"
                aria-label="Notifications">
                <div class="flex flex-col max-h-[500px]">
                    <div class="px-4 py-3 border-b border-surface-100 dark:border-surface-800 flex-shrink-0">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <h3 class="font-semibold text-text-main text-sm">Notifications</h3>
                                @if (notifService.unreadCount() > 0) {
                                    <span class="badge-danger">
                                        {{ notifService.unreadCount() }} new
                                    </span>
                                }
                            </div>
                            @if (notifService.hasUnread()) {
                                <button (click)="markAllRead()"
                                    class="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 font-medium bg-transparent border-none cursor-pointer">
                                    Mark all as read
                                </button>
                            }
                        </div>
                    </div>

                    <div class="overflow-y-auto flex-1 custom-scrollbar" role="list" aria-label="Notification items">
                        @if (notificationVms().length === 0) {
                            <div class="empty-state py-12 px-4">
                                <div class="w-12 h-12 rounded-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center">
                                    <i class="pi pi-bell text-xl empty-state-icon"></i>
                                </div>
                                <p class="empty-state-title">No notifications yet</p>
                                <p class="empty-state-sub">Appraisal activity will appear here</p>
                            </div>
                        } @else {
                            @for (notif of notificationVms(); track notif.id) {
                                <div (click)="onNotifClick(notif.id, notif.actionUrl, notifOp)"
                                    class="px-4 py-3 hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors border-b border-surface-100 dark:border-surface-800 last:border-b-0 cursor-pointer"
                                    [class.opacity-60]="notif.read"
                                    role="listitem">
                                    <div class="flex gap-3">
                                        <div class="flex-shrink-0 mt-0.5">
                                            <div class="w-8 h-8 rounded-full flex items-center justify-center"
                                                [class]="notif.read ? 'bg-surface-100 dark:bg-surface-800' : notif.bgClass">
                                                <i class="text-xs"
                                                    [class]="notif.icon + ' ' + (notif.read ? 'text-text-muted' : notif.iconClass)"></i>
                                            </div>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <p class="text-sm text-text-main font-medium leading-snug">
                                                {{ notif.title }}
                                            </p>
                                            <p class="text-xs text-text-muted mt-0.5 leading-relaxed line-clamp-2">
                                                {{ notif.body }}
                                            </p>
                                            <div class="flex items-center gap-2 mt-1">
                                                <p class="text-xs text-text-muted uppercase font-semibold tracking-wider">
                                                    {{ notif.timeAgo }}
                                                </p>
                                                <span class="text-xs text-surface-300 dark:text-surface-600" aria-hidden="true">·</span>
                                                <p class="text-xs text-text-muted">{{ notif.recipientName }}</p>
                                                @if (notif.actionUrl) {
                                                    <span class="text-xs text-primary-500 dark:text-primary-400 font-medium ml-auto">Open →</span>
                                                }
                                            </div>
                                        </div>
                                        @if (!notif.read) {
                                            <div class="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0 mt-2" aria-label="Unread"></div>
                                        }
                                    </div>
                                </div>
                            }
                        }
                    </div>
                </div>
            </p-popover>
        </div>



        <div class="header-divider mx-1 sm:mx-2"></div>

        <div class="relative">
            <button (click)="op.toggle($event)"
                class="flex items-center gap-2 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors border-none cursor-pointer group py-1.5 px-1 sm:pl-2 sm:pr-3 rounded-lg"
                pRipple
                aria-haspopup="true">

                <p-avatar [label]="currentUserInfo().avatarLabel" size="normal" shape="circle"
                    styleClass="text-white text-xs font-semibold bg-primary-600"></p-avatar>

                <div class="hidden sm:flex flex-col items-start translate-y-[1px]">
                    <span class="text-sm text-text-main font-semibold leading-tight">
                        {{ currentUserInfo().name }}
                    </span>
                    <span class="text-xs text-text-muted leading-tight">
                        {{ currentUserInfo().roleLabel }}
                    </span>
                </div>

                <i
                    class="hidden sm:block pi pi-chevron-down text-xs text-text-muted transition-transform group-hover:translate-y-[1px]"></i>
            </button>

            <p-popover #op styleClass="p-1 border shadow-xl rounded-xl dark:bg-surface-900 dark:border-surface-800 mt-1"
                role="dialog"
                aria-label="User menu">
                <div class="flex flex-col w-56">
                    <div class="sm:hidden px-3.5 py-3 border-b border-surface-100 dark:border-surface-800 mb-1 flex flex-col">
                        <span class="text-sm text-text-main font-bold">
                            {{ currentUserInfo().name }}
                        </span>
                        <span class="text-xs text-text-muted">
                            {{ currentUserInfo().roleLabel }}
                        </span>
                    </div>

                    <button (click)="goToProfile(); op.hide()"
                        class="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-text-muted no-underline cursor-pointer border-none bg-transparent select-none transition-all duration-200 hover:bg-surface-50 dark:hover:bg-surface-800 w-full text-left"
                        pRipple>
                        <i class="pi pi-user text-sm text-text-muted"></i>
                        <span>Profile</span>
                    </button>
                    <button (click)="op.hide()"
                        class="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-text-muted no-underline cursor-pointer border-none bg-transparent select-none transition-all duration-200 hover:bg-surface-50 dark:hover:bg-surface-800 w-full text-left"
                        pRipple>
                        <i class="pi pi-cog text-sm text-text-muted"></i>
                        <span>Settings</span>
                    </button>
                    <div class="border-t border-surface-100 dark:border-surface-800 my-1"></div>
                    <button (click)="handleLogout(); op.hide()"
                        class="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm no-underline cursor-pointer border-none bg-transparent select-none transition-all duration-200 w-full text-left nav-item-danger"
                        pRipple>
                        <i class="pi pi-log-out text-sm"></i>
                        <span>Log out</span>
                    </button>
                </div>
            </p-popover>
        </div>

        <button (click)="layoutService.toggleMobile()"
            class="md:hidden w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg text-text-muted hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors border-none cursor-pointer"
            pRipple
            aria-label="Toggle menu">
            <i class="pi pi-bars text-xl"></i>
        </button>
    </div>
</header>
